/**
 * @webrtc/protocol — public API.
 *
 * Re-exports every zod schema plus inferred TS types and helper functions
 * for parsing and narrowing incoming messages. Consumers should import from
 * this module only (`@webrtc/protocol`), never from internal files.
 */
import { z } from 'zod'
import {
  Message,
  Notification,
  Request,
  Response,
  type ProtocolError as _ProtocolError,
} from './messages.js'

// Re-export every schema for direct use.
export * from './messages.js'

// Guest name generation helper (shared by server and client).
export { ADJECTIVES, NOUNS, generateGuestName } from './guestName.js'

// ---------------------------------------------------------------------------
// Inferred TypeScript types
// ---------------------------------------------------------------------------

export type Message = z.infer<typeof Message>
export type Request = z.infer<typeof Request>
export type Response = z.infer<typeof Response>
export type Notification = z.infer<typeof Notification>
export type ProtocolError = z.infer<typeof _ProtocolError>

// ---------------------------------------------------------------------------
// Narrowing helpers
// ---------------------------------------------------------------------------

export function isRequest(msg: Message): msg is Request {
  return msg.kind === 'request'
}

export function isResponse(msg: Message): msg is Response {
  return msg.kind === 'response'
}

export function isNotification(msg: Message): msg is Notification {
  return msg.kind === 'notification'
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/**
 * Parse an unknown JSON value (or a JSON string) into a typed Message.
 * Returns the parsed message on success, or `null` on failure. Callers
 * that need the detailed zod error should use `Message.safeParse` directly.
 *
 * Server-side: any parse failure should close the WebSocket with code 4400.
 * Client-side: surface as a connection error and likely a bug in the peer.
 */
export function parseMessage(raw: unknown): Message | null {
  let json: unknown = raw
  if (typeof raw === 'string') {
    try {
      json = JSON.parse(raw)
    } catch {
      return null
    }
  }
  const result = Message.safeParse(json)
  return result.success ? result.data : null
}
